import { useAuth } from '@clerk/react'
import type { ColumnDef } from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { Loader2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useLocation } from '#router'
import { FeaturebaseBugButton } from '@/components/featurebase-bug-button'
import { ForkStarButton } from '@/components/problem-lists/fork-star-button'
import { MergeButton } from '@/components/problem-lists/merge-button'
import {
  ProblemListProvider,
  useProblemListsOptional,
} from '@/components/problem-lists/problem-list-context'
import { ProblemListSidebar } from '@/components/problem-lists/problem-list-sidebar'
import { StarDropdown } from '@/components/problem-lists/star-dropdown'
import { DifficultyCell } from '@/components/problems/difficulty-cell'
import { ProblemsFilterPopover } from '@/components/problems/problems-filter-popover'
import { ProblemsSearchBar } from '@/components/problems/problems-search-bar'
import { ProblemsSortPopover } from '@/components/problems/problems-sort-popover'
import {
  applyFilters,
  type BrowseProblem,
  createProblemsFuse,
  deriveAvailableDifficulties,
  deriveAvailableTopics,
  type SortOption,
  searchProblems,
  sortProblems,
} from '@/components/problems/types'
import { ShuffleButton } from '@/components/shuffle-button'
import { ProgressStatusIcon } from '@/components/ui/progress-status-icon'
import { ProgressSummary } from '@/components/ui/progress-summary'
import { usePageTitle } from '@/hooks/use-document-title'
import { useProblemsState } from '@/hooks/use-problems-state'
import { isSolvedStatus } from '@/lib/progress-status'
import { trpc } from '@/lib/trpc'
import type { ListRef } from '@/lib/url-state'
import { cn } from '@/lib/utils'
import '@/styles/problems-table.css'

const CHAPTER_SORT_OPTIONS = ['difficulty', 'questionId', 'status']

const CHAPTER_SORT_POPOVER_OPTIONS = [
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'questionId', label: 'Question ID' },
  { value: 'status', label: 'Status' },
]

export default function ChapterDetailPage() {
  return (
    <ProblemListProvider>
      <ChapterDetailContent />
    </ProblemListProvider>
  )
}

function ChapterDetailContent() {
  const location = useLocation()
  const { navigate } = location
  const chapterSlug =
    location.pathname.match(/^\/problems\/book\/([^/]+)$/)?.[1] ?? ''

  const { isSignedIn } = useAuth()
  const listCtx = useProblemListsOptional()

  // Fetch chapter problems from the database (server-side filtered)
  const { data: chapterData, isLoading } =
    trpc.problems.browseByChapter.useQuery(
      { chapterSlug },
      { enabled: !!chapterSlug },
    )

  const chapterTitle = chapterData?.chapterTitle ?? null
  const problems = chapterData?.problems ?? []

  // Use the slug-derived label as a fallback so the tab title doesn't flash to
  // the site default during the brief gap between mount and tRPC data arrival.
  const chapterFallback = chapterSlug
    ? chapterSlug.replace(/^chapter(\d+)$/i, 'Ch.$1')
    : null
  usePageTitle(chapterTitle ?? chapterFallback)

  // Fetch user progress
  const { data: userProgress } = trpc.problems.getUserProgress.useQuery(
    undefined,
    { enabled: !!isSignedIn },
  )

  // Build list refs for the hook
  const userListRefs: ListRef[] = useMemo(
    () =>
      listCtx
        ? listCtx.lists.map((l) => ({
            id: l.id,
            name: l.name,
            isSystem: l.isSystem,
          }))
        : [],
    [listCtx?.lists, listCtx],
  )

  // Derive available filter values from chapter's problems
  const availableTopics = useMemo(
    () => deriveAvailableTopics(problems),
    [problems],
  )

  const availableDifficulties = useMemo(
    () => deriveAvailableDifficulties(problems),
    [problems],
  )

  const availableLists = useMemo(() => {
    if (!listCtx) return undefined
    return [...listCtx.lists]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((l) => ({ id: l.id, name: l.name }))
  }, [listCtx?.lists, listCtx])

  // Filter/sort/search state
  const {
    filters,
    sortBy,
    sortDir,
    searchQuery,
    matchMode,
    showTags,
    missingLists,
    setSortBy,
    setSortDir,
    setSearchQuery,
    setMatchMode,
    setShowTags,
    toggleFilterValue,
    resetFilters,
    setFilterOperator,
  } = useProblemsState(
    `bctci-chapter-${chapterSlug}`,
    userListRefs,
    availableTopics,
    CHAPTER_SORT_OPTIONS,
  )

  const [dismissed, setDismissed] = useState(false)

  const fuse = useMemo(() => createProblemsFuse(problems), [problems])

  const filteredProblems = useMemo(() => {
    const searched = searchProblems(problems, fuse, searchQuery)
    return applyFilters(
      searched,
      filters,
      matchMode,
      userProgress?.progress,
      listCtx?.memberships,
    )
  }, [
    problems,
    searchQuery,
    fuse,
    filters,
    matchMode,
    userProgress,
    listCtx?.memberships,
  ])

  const sortedProblems = useMemo(
    () =>
      sortProblems(
        filteredProblems,
        sortBy as SortOption,
        sortDir,
        userProgress?.progress,
      ),
    [filteredProblems, sortBy, sortDir, userProgress],
  )

  // Solved count scoped to this chapter's problems
  const solvedCount = useMemo(() => {
    if (!userProgress) return 0
    return problems.filter((p) => isSolvedStatus(userProgress.progress[p.id]))
      .length
  }, [userProgress, problems])

  // Progress data for ProgressSummary
  const progressProblems = useMemo(
    () =>
      problems.map((p) => ({
        id: p.id,
        difficulty: p.difficulty?.name ? { name: p.difficulty.name } : null,
      })),
    [problems],
  )

  // Column definitions
  const columns: ColumnDef<BrowseProblem>[] = useMemo(
    () => [
      {
        accessorKey: 'title',
        header: 'Title',
        cell: ({ row }) => {
          const status = isSignedIn
            ? userProgress?.progress[row.original.id]
            : undefined
          const via = userProgress?.solvedVia?.[row.original.id]
          return (
            <Link
              href={`/editor?problem=${row.original.slug}`}
              className="block"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="inline-flex items-center gap-2 font-medium">
                {isSignedIn && (
                  <span className="inline-flex w-4 shrink-0 justify-center">
                    <ProgressStatusIcon status={status} viaTitle={via?.title} />
                  </span>
                )}
                <span>
                  {row.original.externalId && (
                    <span className="text-muted-foreground">
                      {Number(row.original.externalId)}.{' '}
                    </span>
                  )}
                  {row.original.title}
                </span>
              </span>
              {showTags && row.original.topics.length > 0 && (
                <div
                  className={cn(
                    'mt-1.5 flex flex-wrap gap-1.5',
                    isSignedIn && 'ml-6',
                  )}
                >
                  {row.original.topics.map((topic) => (
                    <span
                      key={topic.slug}
                      className="bg-muted text-muted-foreground rounded px-2 py-0.5 text-xs"
                    >
                      {topic.name}
                    </span>
                  ))}
                </div>
              )}
            </Link>
          )
        },
      },
      {
        id: 'difficulty',
        header: () => <span className="block text-right">Difficulty</span>,
        size: 80,
        cell: ({ row }) => (
          <DifficultyCell difficulty={row.original.difficulty} />
        ),
      },
      {
        id: 'star',
        size: 40,
        header: '',
        cell: ({ row }) => <StarDropdown problemId={row.original.id} />,
      },
    ],
    [isSignedIn, userProgress, showTags],
  )

  const table = useReactTable({
    data: sortedProblems,
    columns,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
  })

  // --- Loading state ---
  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)]">
        <ProblemListSidebar />
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
        </div>
      </div>
    )
  }

  // --- Not found state ---
  if (!chapterTitle) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)]">
        <ProblemListSidebar />
        <div className="flex flex-1 flex-col items-center justify-center gap-4">
          <h2 className="text-lg font-semibold">Chapter not found</h2>
          <p className="text-muted-foreground text-sm">
            This chapter doesn&apos;t exist.
          </p>
          <Link
            href="/problems"
            className="text-primary text-sm hover:underline"
          >
            Back to Problems
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)]">
      <ProblemListSidebar />
      <div className="flex-1 overflow-hidden">
        <div className="mx-auto max-w-7xl px-6 py-8">
          {/* Two-column layout: left = header/progress, right = table */}
          <div className="flex gap-8">
            {/* Left column — icon, chapter name, count, progress */}
            <div className="border-border w-80 shrink-0 self-start rounded-lg border p-5">
              <a
                href="https://www.amazon.com/dp/195570600X"
                target="_blank"
                rel="noopener noreferrer"
                className="from-muted/60 to-muted/20 mb-4 inline-block rounded-2xl bg-gradient-to-br p-3 shadow-sm ring-1 ring-border/50"
              >
                <img
                  src="/images/book-cover.png"
                  alt="Beyond Cracking the Coding Interview"
                  className="h-52 w-auto rounded-xl shadow-xl transition-transform duration-300 hover:scale-[1.02]"
                />
              </a>
              <h1 className="text-2xl font-bold">{chapterTitle}</h1>
              <div className="text-muted-foreground mt-1 text-sm">
                {problems.length} question{problems.length !== 1 ? 's' : ''}
              </div>
              <div className="mt-3 flex items-center gap-2">
                <ForkStarButton sourceType="chapter" sourceId={chapterSlug} />
                <MergeButton sourceType="chapter" sourceId={chapterSlug} />
              </div>
              {/* Divider */}
              <div className="bg-border my-4 h-px w-full" />

              {/* Progress widget */}
              <ProgressSummary
                problems={progressProblems}
                progress={userProgress?.progress}
              />
            </div>

            {/* Right column — toolbar + problem table */}
            <div className="min-w-0 flex-1">
              {/* Missing lists banner */}
              {!dismissed && missingLists.length > 0 && (
                <div className="mb-4 flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200">
                  <span>
                    Some filters reference lists you don&apos;t have — your view
                    may differ.
                  </span>
                  <button
                    type="button"
                    onClick={() => setDismissed(true)}
                    className="ml-4 text-amber-600 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-200"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Toolbar */}
              <div className="mb-4 flex items-center gap-1">
                <ProblemsSearchBar
                  value={searchQuery}
                  onChange={setSearchQuery}
                />
                <ProblemsSortPopover
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSortChange={(newSortBy, newSortDir) => {
                    setSortBy?.(newSortBy)
                    setSortDir?.(newSortDir)
                  }}
                  showTags={showTags}
                  onToggleTags={() => setShowTags((v) => !v)}
                  sortOptions={CHAPTER_SORT_POPOVER_OPTIONS}
                  onReset={
                    sortBy !== 'questionId' || sortDir !== 'asc' || showTags
                      ? () => {
                          setSortBy?.('questionId')
                          setSortDir?.('asc')
                          setShowTags(false)
                        }
                      : undefined
                  }
                />
                <ProblemsFilterPopover
                  filters={filters}
                  matchMode={matchMode}
                  onMatchModeChange={setMatchMode}
                  onToggleFilter={toggleFilterValue}
                  onOperatorChange={setFilterOperator}
                  availableTopics={availableTopics}
                  availableDifficulties={availableDifficulties}
                  availableLists={availableLists}
                  onReset={resetFilters}
                />

                <div className="flex-1" />

                {isSignedIn && <FeaturebaseBugButton />}

                {isSignedIn && userProgress && (
                  <span className="text-muted-foreground inline-flex items-center gap-1.5 text-sm">
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      className="shrink-0"
                    >
                      <circle
                        cx="9"
                        cy="9"
                        r="7"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        opacity="0.25"
                      />
                      <circle
                        cx="9"
                        cy="9"
                        r="7"
                        fill="none"
                        stroke="#429539"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeDasharray={2 * Math.PI * 7}
                        strokeDashoffset={
                          2 *
                          Math.PI *
                          7 *
                          (1 - solvedCount / (problems.length || 1))
                        }
                        transform="rotate(-90 9 9)"
                      />
                    </svg>
                    {solvedCount}/{problems.length} Solved
                  </span>
                )}

                <ShuffleButton
                  problems={sortedProblems}
                  ariaLabel="Solve a random problem from this chapter"
                />
              </div>

              {/* Problems table */}
              <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
                <table className="problems-table w-full">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className={cn(
                              'text-muted-foreground py-2 text-left text-xs font-medium',
                              header.column.id === 'title'
                                ? 'px-4'
                                : 'px-2 whitespace-nowrap',
                            )}
                            style={
                              header.column.id !== 'title'
                                ? { width: '1%' }
                                : undefined
                            }
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.length ? (
                      table.getRowModel().rows.map((row) => (
                        <tr
                          key={row.id}
                          onClick={(e) => {
                            if ((e.target as HTMLElement).closest('a, button'))
                              return
                            navigate(`/editor?problem=${row.original.slug}`)
                          }}
                          className="cursor-pointer transition-colors"
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              className={cn(
                                'py-3 text-sm',
                                cell.column.id === 'title'
                                  ? 'px-4'
                                  : 'px-2 whitespace-nowrap',
                              )}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext(),
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={columns.length}
                          className="text-muted-foreground py-10 text-center"
                        >
                          No problems found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
